/**
 * ContratsCorbeille — Corbeille des contrats supprimés.
 * Restauration en 1 clic (individuelle) ou multi-sélection (bulk restore).
 * Affiche l'auteur et la raison de suppression (audit trail visible).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, RotateCcw, Trash2, FileText, Info, User, MessageSquare, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const fmtEUR = (v) => (v == null ? "-" : new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v));
const fmtDateTime = (d) => d ? new Date(d).toLocaleString("fr-FR", { timeZone: "Europe/Paris" }) : "-";

export default function ContratsCorbeille() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkRestoring, setBulkRestoring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/contracts/trash");
      setContracts(r.data.contracts || []);
      setSelected(new Set());
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const allSelected = useMemo(() =>
    contracts.length > 0 && selected.size === contracts.length,
    [contracts.length, selected.size]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(contracts.map((c) => c.id)));
  };

  const restore = async (c) => {
    const name = c.societe || c.party_name || "ce contrat";
    if (!window.confirm(`Restaurer le contrat de « ${name} » ?`)) return;
    setRestoring(c.id);
    try {
      await api.post(`/contracts/${c.id}/restore`);
      toast.success("Contrat restauré");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRestoring(null); }
  };

  const bulkRestore = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(
      `Restaurer ${selected.size} contrat${selected.size > 1 ? "s" : ""} ?`
    )) return;
    setBulkRestoring(true);
    try {
      const ids = Array.from(selected);
      const r = await api.post("/contracts/bulk-restore", { ids });
      toast.success(
        `${r.data.restored} contrat${r.data.restored > 1 ? "s" : ""} restauré${r.data.restored > 1 ? "s" : ""}`
        + (r.data.skipped?.length ? ` · ${r.data.skipped.length} ignoré(s)` : "")
      );
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkRestoring(false); }
  };

  return (
    <div className="space-y-5" data-testid="contracts-trash-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Contrats</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Corbeille
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {contracts.length} contrat{contracts.length > 1 ? "s" : ""} supprimé{contracts.length > 1 ? "s" : ""}.
            Restauration en 1 clic possible tant que la purge définitive n'a pas eu lieu.
          </p>
        </div>
        <Button variant="ghost" onClick={() => navigate("/app/contracts/annuaire")}
                data-testid="trash-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={2} />
          Retour aux contrats actifs
        </Button>
      </div>

      {/* Toolbar sélection multiple */}
      {selected.size > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 flex items-center justify-between">
          <div className="text-sm text-blue-900">
            <b>{selected.size}</b> contrat{selected.size > 1 ? "s" : ""} sélectionné{selected.size > 1 ? "s" : ""}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}
                    className="text-blue-700 hover:bg-blue-100"
                    data-testid="trash-clear-selection">
              Tout désélectionner
            </Button>
            <Button size="sm" onClick={bulkRestore} disabled={bulkRestoring}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="trash-bulk-restore-btn">
              {bulkRestoring ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                              : <RotateCcw className="w-4 h-4 mr-1.5" />}
              Restaurer la sélection
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-400">Chargement…</div>
          ) : contracts.length === 0 ? (
            <div className="p-12 text-center">
              <Trash2 className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">La corbeille est vide.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 text-xs uppercase text-slate-500">
                  <TableHead className="w-8">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll}
                              data-testid="trash-select-all" />
                  </TableHead>
                  <TableHead>Contrat</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Montant HT</TableHead>
                  <TableHead>Supprimé</TableHead>
                  <TableHead>Raison</TableHead>
                  <TableHead className="text-right w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contracts.map((c) => (
                  <TableRow key={c.id} data-testid={`trash-row-${c.id}`}
                            className={selected.has(c.id) ? "bg-blue-50" : ""}>
                    <TableCell>
                      <Checkbox checked={selected.has(c.id)}
                                onCheckedChange={() => toggle(c.id)}
                                data-testid={`trash-select-${c.id}`} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="h-3.5 w-3.5 text-slate-400" />
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {c.societe || c.party_name || c.title || "—"}
                          </div>
                          {c.reference && (
                            <div className="text-[11px] text-slate-500 font-mono">{c.reference}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[11px]">
                        {c.type || c.contract_type || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {fmtEUR(c.amount_ht ?? c.montant_abonnement)}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      <div className="text-slate-700">{fmtDateTime(c.deleted_at)}</div>
                      {c.deleted_by && (
                        <div className="flex items-center gap-1 text-slate-500 mt-0.5">
                          <User className="h-3 w-3" />
                          <span className="truncate max-w-[180px]" title={c.deleted_by}>
                            {c.deleted_by}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs max-w-[260px]">
                      {c.deletion_reason ? (
                        <div className="flex items-start gap-1 text-slate-700">
                          <MessageSquare className="h-3 w-3 mt-0.5 shrink-0 text-slate-400" />
                          <span className="line-clamp-2" title={c.deletion_reason}>
                            {c.deletion_reason}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm"
                              onClick={() => restore(c)}
                              disabled={restoring === c.id}
                              data-testid={`restore-btn-${c.id}`}
                              className="h-8 text-xs">
                        <RotateCcw className={`h-3.5 w-3.5 mr-1 ${restoring === c.id ? "animate-spin" : ""}`} />
                        Restaurer
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="text-[11px] text-slate-400 flex items-start gap-1.5">
        <Info className="h-3 w-3 mt-0.5 shrink-0" />
        <span>
          Les contrats supprimés sont conservés pour permettre une restauration. Une purge
          définitive automatique est possible après un délai (voir Paramètres).
        </span>
      </div>
    </div>
  );
}
